import React, { useCallback, useEffect, useReducer, useRef } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import ProfileImage from "../../Common/ProfileImage";
import { getConversationHistoryTime, getMentionNameFromRoaster } from "../../../../Helpers/Utility";
import { getUserDetailsForMention } from "../../../../Helpers/Chat/User";
import { getFromLocalStorageAndDecrypt } from "../../WebChatEncryptDecrypt";
import { CHAT_TYPE_GROUP, CHAT_TYPE_SINGLE } from "../../../../Helpers/Chat/Constant";
import { GLOBAL_SEARCH_TYPE } from "../../../../api/globalSearch";
import { decryptMessageBody, decryptName } from "../../../../api/globalSearch/decrypt";
import {
    getGroupId,
    resolveChatIdentity,
    resolveDisplayName,
    resolveResultTarget,
    resolveRoster,
    toBareId
} from "./globalSearchNavigation";

const MEDIA_LABELS = {
    image: "Photo",
    video: "Video",
    audio: "Audio",
    file: "Document",
    location: "Location",
    contact: "Contact",
    meet: "Meeting"
};

/**
 * Mentions are stored as "@[?]" placeholders, one per tagged user, filled from
 * mentionedUsersIds in order. Replace each with "@<name>" so the preview shows the tagged
 * user instead of the raw placeholder.
 */
const resolveMentions = (text = "", mentionedUsersIds = []) => {
    if (!text || !Array.isArray(mentionedUsersIds) || !mentionedUsersIds.length) return text;
    let index = 0;
    return text.replace(/@\[\?\]/g, () => {
        const userId = mentionedUsersIds[index++];
        if (!userId) return "@[?]";
        const name = getMentionNameFromRoaster(getUserDetailsForMention(userId)) || userId;
        return `@${name}`;
    });
};

/**
 * A message body is stored as HTML — spaces can be `&nbsp;`, and mentions/formatting are
 * tags. Rendered raw, the entities are lost and the text runs together (e.g. "Drop@me").
 * Parse it to plain text so `&nbsp;` becomes a real space and tags are stripped to their
 * text, then collapse runs of whitespace for a clean single-line preview.
 */
const toPlainPreview = (value = "") => {
    if (typeof value !== "string" || !value) return "";
    if (!/[<&]/.test(value)) return value; // no markup/entities — nothing to normalise
    const tmp = document.createElement("div");
    tmp.innerHTML = value;
    const text = tmp.textContent || tmp.innerText || "";
    // \s also matches the non-breaking space &nbsp; decodes to — normalise and collapse.
    return text.replace(/\s+/g, " ").trim();
};

const HIGHLIGHT_COLOR = "#005386";
const NBSP = " ";

/**
 * Highlights the search term while preserving spacing. The shared getHighlightedText splits
 * the text into sibling <span>s, and a space at a span boundary collapses when rendered
 * (so "Hi Rajesh" reads as "HiRajesh"). Rendering a boundary space as a non-breaking space
 * keeps it — safe here because the preview is a single, non-wrapping line.
 */
const highlightPreview = (text = "", term = "") => {
    if (!text) return text;
    const needle = (term || "").trim();
    if (!needle) return text;
    const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts = String(text).split(new RegExp(`(${escaped})`, "gi"));
    return parts.map((part, index) => {
        const isMatch = part.toLowerCase() === needle.toLowerCase();
        // Only a space touching a span edge collapses, so protect just those.
        const preserved = part.replace(/^ /, NBSP).replace(/ $/, NBSP);
        return (
            <span key={index} style={isMatch ? { color: HIGHLIGHT_COLOR } : undefined}>
                {preserved}
            </span>
        );
    });
};

/**
 * Flattens a search result of any tab into the fields the row renders.
 * Names always come from the local roster/group data first — the API's name fields are
 * encrypted with an unknown key, so they are only a fallback.
 */
const getViewModel = (item, type, onNameResolved) => {
    if (type === GLOBAL_SEARCH_TYPE.CONTACTS) {
        // A contacts result can be a group; render it as a group (avatar + name) so it does
        // not masquerade as a 1-to-1 chat.
        const { chatId, chatType } = resolveResultTarget(item, type);
        const isGroup = chatType === CHAT_TYPE_GROUP;
        const encryptedName = isGroup ? item.groupName || item.nickname : item.nickname;
        return {
            name: resolveDisplayName(chatId, chatType, decryptName(encryptedName, chatId), onNameResolved),
            subText: "",
            time: "",
            chatType,
            userId: chatId,
            imageToken: item.thumbImage || item.image
        };
    }

    if (type === GLOBAL_SEARCH_TYPE.GROUPS) {
        const groupId = getGroupId(item);
        return {
            name: resolveDisplayName(groupId, CHAT_TYPE_GROUP, decryptName(item.groupName, groupId)),
            subText: "",
            time: "",
            chatType: CHAT_TYPE_GROUP,
            userId: groupId,
            imageToken: item.thumbImage || item.image
        };
    }

    const { chatId, chatType } = resolveChatIdentity(item);
    const roster = resolveRoster(item, chatId, chatType);
    const isGroupChat = chatType === CHAT_TYPE_GROUP;
    const fallbackName = roster?.groupName || roster?.name || roster?.nickName || "";
    const name = resolveDisplayName(chatId, chatType, fallbackName, onNameResolved);

    // The decrypted body carries both the text and the sender's name in plaintext.
    const { text, nickName, mentionedUsersIds } = decryptMessageBody(item.message, item.messageId);
    // The tag ids may sit on the decrypted body or on the search result itself.
    const mentionIds = mentionedUsersIds.length ? mentionedUsersIds : item.mentionedUsersIds || [];
    const plainText = toPlainPreview(resolveMentions(text, mentionIds));
    const preview =
        item.messageType && item.messageType !== "text"
            ? plainText || MEDIA_LABELS[item.messageType] || ""
            : plainText;
    // In a group, a message hit is more useful when it names the sender.
    const senderName = isGroupChat
        ? nickName || resolveDisplayName(toBareId(item.fromUser), CHAT_TYPE_SINGLE, "", onNameResolved)
        : "";

    return {
        name,
        subText: isGroupChat && senderName ? `${senderName}: ${preview}` : preview,
        time: getConversationHistoryTime(item.sentTime),
        chatType,
        userId: chatId,
        imageToken: roster?.thumbImage || roster?.image || roster?.groupImage
    };
};

const GlobalSearchResultItem = ({ item, type, searchTerm, onClick }) => {
    const token = getFromLocalStorageAndDecrypt("token");
    // Bumped when a background profile fetch fills in a name that was missing locally.
    const [, forceRender] = useReducer((count) => count + 1, 0);
    const mountedRef = useRef(true);
    useEffect(() => () => { mountedRef.current = false; }, []);
    const onNameResolved = useCallback(() => {
        if (mountedRef.current) forceRender();
    }, []);

    const { name, subText, time, chatType, userId, imageToken } = getViewModel(item, type, onNameResolved);
    const isMessageHit = type === GLOBAL_SEARCH_TYPE.MESSAGES;

    // Highlight the row whose chat is currently open, mirroring the recent-chat list. Not for
    // message hits — many messages share one conversation, so they would all highlight at once.
    const activeChatId = useSelector((state) => state.activeChatData?.data?.chatId);
    const isActive = !isMessageHit && userId && userId === activeChatId;

    return (
        <li
            className={`chat-list-li global-search-item ${isActive ? "active" : ""}`}
            tabIndex="0"
            role="button"
            onClick={() => onClick(item)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick(item)}
        >
            <ProfileImage
                chatType={chatType}
                userToken={token}
                userId={userId}
                imageToken={imageToken}
                name={name}
            />
            <div className="recentchats">
                <div className="recent-username-block">
                    <div className="recent-username">
                        <div className="username">
                            <h3 title={name}>{isMessageHit ? name : highlightPreview(name, searchTerm)}</h3>
                        </div>
                    </div>
                    {time && <span className="recent-chat-time">{time}</span>}
                </div>
                {subText && (
                    <div className="recent-message-block">
                        <span className="recent-message" title={subText}>
                            {isMessageHit ? highlightPreview(subText, searchTerm) : subText}
                        </span>
                    </div>
                )}
            </div>
        </li>
    );
};

GlobalSearchResultItem.propTypes = {
    item: PropTypes.object,
    type: PropTypes.string,
    searchTerm: PropTypes.string,
    onClick: PropTypes.func
};

export default GlobalSearchResultItem;
